import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./ApplicationManagement.css";
import {
  getAllApplications,
  scheduleInterview,
  updateApplicationStatus,
} from "../services/applicationService";

const PAGE_SIZE = 10;

const STATUS_OPTIONS = [
  "APPLIED",
  "SHORTLISTED",
  "INTERVIEW_SCHEDULED",
  "INTERVIEW_COMPLETED",
  "SELECTED",
  "OFFER_RELEASED",
  "REJECTED",
];

const FINAL_STATUSES = new Set(["SELECTED", "REJECTED", "OFFER_RELEASED"]);

const BADGE_CLASS_MAP = {
  APPLIED: "application-status-applied",
  SHORTLISTED: "application-status-shortlisted",
  INTERVIEW_SCHEDULED: "application-status-interview-scheduled",
  INTERVIEW_COMPLETED: "application-status-interview-completed",
  SELECTED: "application-status-selected",
  REJECTED: "application-status-rejected",
  OFFER_RELEASED: "application-status-offer-released",
  JOINED: "application-status-joined",
};

const formatStatusLabel = (status) =>
  String(status || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDateLabel = (value) => {
  if (!value || value === "-") return "-";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleDateString("en-GB");
};

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, index) =>
  String(index + 1).padStart(2, "0"),
);

const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, index) =>
  String(index).padStart(2, "0"),
);

const getTimeParts = (value) => {
  const match = String(value || "")
    .trim()
    .match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

  if (!match) {
    return {
      hour: "",
      minute: "",
      meridiem: "",
    };
  }

  return {
    hour: match[1].padStart(2, "0"),
    minute: match[2],
    meridiem: match[3].toUpperCase(),
  };
};

const buildInterviewTime = ({ hour, minute, meridiem }) => {
  if (!hour || !minute || !meridiem) {
    return "";
  }

  return `${hour}:${minute} ${meridiem}`;
};

const isValidUrl = (value) => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const toDisplayText = (value) => {
  if (Array.isArray(value)) {
    const joined = value
      .map((entry) => String(entry || "").trim())
      .filter(Boolean)
      .join(", ");

    return joined || "-";
  }

  const text = String(value || "").trim();
  return text || "-";
};

const formatBooleanLabel = (value) => {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  const normalized = String(value || "").trim().toLowerCase();

  if (!normalized) {
    return "-";
  }

  if (normalized === "true" || normalized === "yes") {
    return "Yes";
  }

  if (normalized === "false" || normalized === "no") {
    return "No";
  }

  return String(value);
};

const normalizeRecords = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((entry) => entry && typeof entry === "object");
};

const normalizeComparable = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const isFilterableJobTitle = (title) => {
  const normalizedTitle = normalizeComparable(title);
  return normalizedTitle && normalizedTitle !== "-" && normalizedTitle !== "archived role";
};

const stripPrimaryEducationRecord = (records, item) => {
  const primaryMatchIndex = records.findIndex((record) => {
    return (
      normalizeComparable(record.isHigherEducation) ===
        normalizeComparable(item.isHigherEducation) &&
      normalizeComparable(record.qualification) ===
        normalizeComparable(item.qualification) &&
      normalizeComparable(record.specialization) ===
        normalizeComparable(item.specialization) &&
      normalizeComparable(record.educationType) ===
        normalizeComparable(item.educationType) &&
      normalizeComparable(record.collegeInstituteName) ===
        normalizeComparable(item.collegeInstituteName) &&
      normalizeComparable(record.universityName) ===
        normalizeComparable(item.universityName) &&
      normalizeComparable(record.educationFromDate) ===
        normalizeComparable(item.educationFromDate) &&
      normalizeComparable(record.educationEndDate) ===
        normalizeComparable(item.educationEndDate)
    );
  });

  if (primaryMatchIndex === -1) {
    return records;
  }

  return records.filter((_, index) => index !== primaryMatchIndex);
};

const getStatusBadgeClassName = (status) =>
  BADGE_CLASS_MAP[String(status || "").toUpperCase()] ||
  "application-status-default";

const normalizeApplication = (item) => {
  const fullName =
    item.fullName ||
    [item.firstName, item.middleName, item.lastName]
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(" ") ||
    "-";

  const educationRecords = normalizeRecords(item.educations);

  return {
    applicationId: item.applicationId || item.id || item._id || "",
    title: toDisplayText(item.title),
    firstName: toDisplayText(item.firstName),
    middleName: toDisplayText(item.middleName),
    lastName: toDisplayText(item.lastName),
    fullName,
    dateOfBirth: item.dateOfBirth || "",
    email: item.email || "-",
    phone: item.phone || "-",
    secondaryPhone: item.secondaryPhone || "-",
    totalExperience: toDisplayText(item.totalExperience),
    currentCountry: toDisplayText(item.currentCountry),
    currentAddressLine1: toDisplayText(item.currentAddressLine1),
    currentAddressLine2: toDisplayText(item.currentAddressLine2),
    currentCity: toDisplayText(item.currentCity),
    currentState: toDisplayText(item.currentState),
    currentZipCode: toDisplayText(item.currentZipCode),
    isHigherEducation: formatBooleanLabel(item.isHigherEducation),
    jobTitle: item.jobTitle || item.title || item.job?.title || "-",
    jobCode: item.jobCode || item.job?.job_code || "-",
    jobLocation: item.location || item.job?.location || "-",
    qualification: toDisplayText(item.qualification),
    specialization: toDisplayText(item.specialization),
    educationType: toDisplayText(item.educationType),
    collegeInstituteName: toDisplayText(item.collegeInstituteName),
    universityName: toDisplayText(item.universityName),
    educationFromDate: item.educationFromDate || "",
    educationEndDate: item.educationEndDate || "",
    skills: toDisplayText(item.skills),
    otherSkillSet: toDisplayText(item.otherSkillSet),
    linkedin: item.linkedin || item.linkedinUrl || "",
    portfolio: item.portfolio || item.githubUrl || "",
    educations: stripPrimaryEducationRecord(educationRecords, item),
    certifications: normalizeRecords(item.certifications),
    resumeUrl: item.resumeUrl || "",
    status: String(item.status || "APPLIED").toUpperCase(),
    createdAt: item.createdAt || item.appliedOn || item.appliedDate || "",
    interviewDate: item.interview?.date || "",
    interviewTime: item.interview?.time || "",
    interviewType: item.interview?.interviewType || "VIRTUAL",
    meetingLink: item.interview?.meetingLink || "",
    interviewLocation: item.interview?.location || "",
  };
};

const DetailField = ({ label, value }) => (
  <div className="application-detail-field">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const ApplicationManagement = () => {
  const [applications, setApplications] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [jobFilter, setJobFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeCandidateId, setActiveCandidateId] = useState(null);
  const [detailStatus, setDetailStatus] = useState("");
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [interviewSchedule, setInterviewSchedule] = useState({
    date: "",
    hour: "",
    minute: "",
    meridiem: "",
    interviewType: "VIRTUAL",
    meetingLink: "",
    location: "",
  });

  const fetchApplications = useCallback(async () => {
    try {
      const rows = await getAllApplications();
      const normalized = (Array.isArray(rows) ? rows : []).map(
        normalizeApplication,
      ).filter((application) => isFilterableJobTitle(application.jobTitle));

      setApplications(normalized);
    } catch (error) {
      console.error("Failed to fetch applications:", error);
      setApplications([]);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const activeCandidate = useMemo(
    () =>
      applications.find(
        (application) => application.applicationId === activeCandidateId,
      ) || null,
    [activeCandidateId, applications],
  );

  useEffect(() => {
    setDetailStatus(activeCandidate?.status || "");
    setStatusError("");
    setFieldErrors({});

    const nextTimeParts = getTimeParts(activeCandidate?.interviewTime);

    setInterviewSchedule({
      date: activeCandidate?.interviewDate
        ? String(activeCandidate.interviewDate).split("T")[0]
        : "",
      hour: nextTimeParts.hour,
      minute: nextTimeParts.minute,
      meridiem: nextTimeParts.meridiem,
      interviewType: activeCandidate?.interviewType || "VIRTUAL",
      meetingLink: activeCandidate?.meetingLink || "",
      location: activeCandidate?.interviewLocation || "",
    });
  }, [activeCandidate]);

  const isInterviewScheduledStatus = detailStatus === "INTERVIEW_SCHEDULED";

  const interviewDetailsChanged = useMemo(() => {
    if (!activeCandidate) {
      return false;
    }

    const originalTimeParts = getTimeParts(activeCandidate.interviewTime);

    return (
      (activeCandidate.interviewDate
        ? String(activeCandidate.interviewDate).split("T")[0]
        : "") !== interviewSchedule.date ||
      originalTimeParts.hour !== interviewSchedule.hour ||
      originalTimeParts.minute !== interviewSchedule.minute ||
      originalTimeParts.meridiem !== interviewSchedule.meridiem ||
      (activeCandidate.interviewType || "VIRTUAL") !==
        interviewSchedule.interviewType ||
      (activeCandidate.meetingLink || "") !== interviewSchedule.meetingLink ||
      (activeCandidate.interviewLocation || "") !== interviewSchedule.location
    );
  }, [activeCandidate, interviewSchedule]);

  const jobOptions = useMemo(
    () => [
      "All",
      ...new Set(
        applications
          .map((application) => application.jobTitle)
          .filter(isFilterableJobTitle),
      ),
    ],
    [applications],
  );

  const summary = useMemo(() => {
    const shortlisted = applications.filter(
      (application) => application.status === "SHORTLISTED",
    ).length;

    const selected = applications.filter(
      (application) => application.status === "SELECTED",
    ).length;

    const rejected = applications.filter(
      (application) => application.status === "REJECTED",
    ).length;

    return {
      total: applications.length,
      shortlisted,
      selected,
      rejected,
    };
  }, [applications]);

  const filteredApplications = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    return applications.filter((application) => {
      const matchesSearch =
        !query ||
        application.fullName.toLowerCase().includes(query) ||
        application.email.toLowerCase().includes(query) ||
        application.jobTitle.toLowerCase().includes(query) ||
        application.jobCode.toLowerCase().includes(query) ||
        formatStatusLabel(application.status).toLowerCase().includes(query);

      const matchesJob =
        jobFilter === "All" || application.jobTitle === jobFilter;

      const matchesStatus =
        statusFilter === "All" || application.status === statusFilter;

      return matchesSearch && matchesJob && matchesStatus;
    });
  }, [applications, jobFilter, searchText, statusFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredApplications.length / PAGE_SIZE),
  );

  const paginatedApplications = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredApplications.slice(startIndex, startIndex + PAGE_SIZE);
  }, [currentPage, filteredApplications]);

  useEffect(() => {
    setCurrentPage(1);
  }, [jobFilter, searchText, statusFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleInterviewFieldChange = (field, value) => {
    setStatusError("");
    setFieldErrors((prev) => ({
      ...prev,
      [field]: "",
    }));

    setInterviewSchedule((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateInterviewSchedule = () => {
    const errors = {};

    if (!interviewSchedule.date) {
      errors.date = "Interview date is required.";
    } else {
      const selectedDate = new Date(`${interviewSchedule.date}T00:00:00`);

      if (Number.isNaN(selectedDate.getTime())) {
        errors.date = "Interview date is invalid.";
      }
    }

    if (!interviewSchedule.hour) {
      errors.hour = "Select an hour.";
    }

    if (!interviewSchedule.minute) {
      errors.minute = "Select minutes.";
    }

    if (!interviewSchedule.meridiem) {
      errors.meridiem = "Select AM or PM.";
    }

    if (interviewSchedule.interviewType === "VIRTUAL") {
      if (!interviewSchedule.meetingLink.trim()) {
        errors.meetingLink = "Meeting link is required.";
      } else if (!isValidUrl(interviewSchedule.meetingLink.trim())) {
        errors.meetingLink = "Enter a valid meeting link.";
      }
    }

    if (interviewSchedule.interviewType === "OFFLINE" && !interviewSchedule.location.trim()) {
      errors.location = "Interview location is required.";
    }

    return errors;
  };

  const handleSaveStatus = async () => {
    if (
      !activeCandidate?.applicationId ||
      !detailStatus ||
      FINAL_STATUSES.has(activeCandidate.status)
    ) {
      return;
    }

    try {
      setStatusError("");
      setFieldErrors({});
      setIsSavingStatus(true);

      if (detailStatus === "INTERVIEW_SCHEDULED") {
        const errors = validateInterviewSchedule();

        if (Object.keys(errors).length > 0) {
          setFieldErrors(errors);
          setStatusError("Please complete all interview scheduling fields.");
          return;
        }

        await scheduleInterview(activeCandidate.applicationId, {
          interviewType: interviewSchedule.interviewType,
          date: interviewSchedule.date,
          time: buildInterviewTime(interviewSchedule),
          meetingLink:
            interviewSchedule.interviewType === "VIRTUAL"
              ? interviewSchedule.meetingLink.trim()
              : "",
          location:
            interviewSchedule.interviewType === "OFFLINE"
              ? interviewSchedule.location.trim()
              : "",
        });
      }

      await updateApplicationStatus(activeCandidate.applicationId, detailStatus);

      await fetchApplications();
    } catch (error) {
      console.error("Status update failed:", error);
      const message =
        error?.response?.data?.message || "Failed to update status";
      setStatusError(message);
      alert(message);
    } finally {
      setIsSavingStatus(false);
    }
  };

  const openResume = (resumeUrl) => {
    if (!resumeUrl) {
      alert("Resume not available");
      return;
    }

    window.open(resumeUrl, "_blank", "noopener,noreferrer");
  };

  if (activeCandidate) {
    const isStatusChanged = detailStatus !== activeCandidate.status;
    const detailStatusOptions = STATUS_OPTIONS.includes(activeCandidate.status)
      ? STATUS_OPTIONS
      : [activeCandidate.status, ...STATUS_OPTIONS];
    const canSaveStatus =
      !FINAL_STATUSES.has(activeCandidate.status) &&
      (isStatusChanged ||
        (isInterviewScheduledStatus && interviewDetailsChanged));

    return (
      <div className="application-admin-page">
        <header className="application-admin-header">
          <div>
            <h2 className="application-admin-title">
              Candidate Application Details
            </h2>
            <p className="application-admin-subtitle">
              Review {activeCandidate.fullName}'s application and manage the
              application status.
            </p>
          </div>

          <button
            type="button"
            className="application-secondary-btn"
            onClick={() => setActiveCandidateId(null)}
          >
            Back to Applications
          </button>
        </header>

        <section className="application-detail-layout">
          <div className="application-detail-main">
            <article className="application-detail-card">
              <div className="application-card-head">
                <h3>1. My Documents</h3>
                <span
                  className={`application-status-badge ${getStatusBadgeClassName(
                    activeCandidate.status,
                  )}`}
                >
                  {formatStatusLabel(activeCandidate.status)}
                </span>
              </div>

              <div className="application-document-row">
                <DetailField
                  label="Resume"
                  value={activeCandidate.resumeUrl ? "Available" : "-"}
                />
                <DetailField
                  label="Applied On"
                  value={formatDateLabel(activeCandidate.createdAt)}
                />
              </div>

              {activeCandidate.certifications.length > 0 ? (
                <div className="application-record-list">
                  {activeCandidate.certifications.map((certification, index) => (
                    <article
                      key={`certification-${index}`}
                      className="application-record-card"
                    >
                      <h4>Certification / License {index + 1}</h4>

                      <div className="application-record-grid">
                        <DetailField
                          label="Certification Name"
                          value={toDisplayText(certification.certificationName)}
                        />
                        <DetailField
                          label="Institution"
                          value={toDisplayText(certification.institution)}
                        />
                        <DetailField
                          label="Effective Date"
                          value={formatDateLabel(certification.effectiveDate)}
                        />
                        <DetailField
                          label="Expiration Date"
                          value={formatDateLabel(certification.expirationDate)}
                        />
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="application-empty-copy">
                  No certifications or licenses available.
                </p>
              )}
            </article>

            <article className="application-detail-card">
              <div className="application-card-head">
                <h3>2. Profile Information</h3>
              </div>

              <div className="application-detail-grid">
                <DetailField label="Title" value={activeCandidate.title} />
                <DetailField label="First Name" value={activeCandidate.firstName} />
                <DetailField label="Middle Name" value={activeCandidate.middleName} />
                <DetailField label="Last Name" value={activeCandidate.lastName} />
                <DetailField
                  label="Date of Birth"
                  value={formatDateLabel(activeCandidate.dateOfBirth)}
                />
                <DetailField
                  label="Primary Phone Number"
                  value={activeCandidate.phone}
                />
                <DetailField
                  label="Primary Email ID"
                  value={activeCandidate.email}
                />
                <DetailField
                  label="Secondary Phone"
                  value={activeCandidate.secondaryPhone}
                />
                <DetailField
                  label="Total Experience"
                  value={activeCandidate.totalExperience}
                />
                <DetailField
                  label="Country of Residence"
                  value={activeCandidate.currentCountry}
                />
                <DetailField
                  label="Address Line 1"
                  value={activeCandidate.currentAddressLine1}
                />
                <DetailField
                  label="Address Line 2"
                  value={activeCandidate.currentAddressLine2}
                />
                <DetailField label="City" value={activeCandidate.currentCity} />
                <DetailField
                  label="State / Province / Region"
                  value={activeCandidate.currentState}
                />
                <DetailField
                  label="Zip Code"
                  value={activeCandidate.currentZipCode}
                />
              </div>
            </article>

            <article className="application-detail-card">
              <div className="application-card-head">
                <h3>3. Education</h3>
              </div>

              <div className="application-detail-grid">
                <DetailField
                  label="Is this your higher education?"
                  value={activeCandidate.isHigherEducation}
                />
                <DetailField
                  label="Qualification"
                  value={activeCandidate.qualification}
                />
                <DetailField
                  label="Specialization"
                  value={activeCandidate.specialization}
                />
                <DetailField
                  label="Education Type"
                  value={activeCandidate.educationType}
                />
                <DetailField
                  label="College / Institute Name"
                  value={activeCandidate.collegeInstituteName}
                />
                <DetailField
                  label="University Name"
                  value={activeCandidate.universityName}
                />
                <DetailField
                  label="From Date"
                  value={formatDateLabel(activeCandidate.educationFromDate)}
                />
                <DetailField
                  label="End Date"
                  value={formatDateLabel(activeCandidate.educationEndDate)}
                />
              </div>

              {activeCandidate.educations.length > 0 && (
                <div className="application-record-list application-record-list-spaced">
                  {activeCandidate.educations.map((education, index) => (
                    <article
                      key={`education-${index}`}
                      className="application-record-card"
                    >
                      <h4>Additional Education {index + 1}</h4>

                      <div className="application-record-grid">
                        <DetailField
                          label="Is this your higher education?"
                          value={formatBooleanLabel(education.isHigherEducation)}
                        />
                        <DetailField
                          label="Qualification"
                          value={toDisplayText(education.qualification)}
                        />
                        <DetailField
                          label="Specialization"
                          value={toDisplayText(education.specialization)}
                        />
                        <DetailField
                          label="Education Type"
                          value={toDisplayText(education.educationType)}
                        />
                        <DetailField
                          label="College / Institute Name"
                          value={toDisplayText(education.collegeInstituteName)}
                        />
                        <DetailField
                          label="University Name"
                          value={toDisplayText(education.universityName)}
                        />
                        <DetailField
                          label="From Date"
                          value={formatDateLabel(education.educationFromDate)}
                        />
                        <DetailField
                          label="End Date"
                          value={formatDateLabel(education.educationEndDate)}
                        />
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </article>

            <article className="application-detail-card">
              <div className="application-card-head">
                <h3>4. Job-Specific Information</h3>
              </div>

              <div className="application-detail-grid">
                <DetailField label="Skills" value={activeCandidate.skills} />
                <DetailField
                  label="Skills (If Other, mention the skillset below)"
                  value={activeCandidate.otherSkillSet}
                />
                <DetailField
                  label="GitHub / Portfolio"
                  value={activeCandidate.portfolio || "-"}
                />
              </div>

              {activeCandidate.portfolio && (
                <div className="application-link-list">
                  <a
                    href={activeCandidate.portfolio}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open GitHub / Portfolio
                  </a>
                </div>
              )}
            </article>
          </div>

          <aside className="application-detail-card application-detail-side-card">
            <div className="application-card-head">
              <h3>Actions</h3>
            </div>

            <label className="application-field-label" htmlFor="detailStatus">
              Update Status
            </label>

            <select
              id="detailStatus"
              value={detailStatus}
              disabled={FINAL_STATUSES.has(activeCandidate.status)}
              onChange={(event) => {
                setStatusError("");
                setDetailStatus(event.target.value);
              }}
            >
              {detailStatusOptions.map((status) => (
                <option key={status} value={status}>
                  {formatStatusLabel(status)}
                </option>
              ))}
            </select>

            {isInterviewScheduledStatus && (
              <div className="application-schedule-panel">
                <div className="application-schedule-grid">
                  <div className="application-field-group">
                    <label
                      className="application-field-label"
                      htmlFor="interviewDate"
                    >
                      Interview Date
                    </label>
                    <input
                      id="interviewDate"
                      type="date"
                      value={interviewSchedule.date}
                      className={fieldErrors.date ? "application-input-error" : ""}
                      onChange={(event) =>
                        handleInterviewFieldChange("date", event.target.value)
                      }
                    />
                    {fieldErrors.date && (
                      <p className="application-field-error">{fieldErrors.date}</p>
                    )}
                  </div>

                  <div className="application-field-group">
                    <span className="application-field-label">Interview Time</span>
                    <div className="application-time-grid">
                      <select
                        value={interviewSchedule.hour}
                        className={fieldErrors.hour ? "application-input-error" : ""}
                        onChange={(event) =>
                          handleInterviewFieldChange("hour", event.target.value)
                        }
                      >
                        <option value="">Hour</option>
                        {HOUR_OPTIONS.map((hour) => (
                          <option key={hour} value={hour}>
                            {hour}
                          </option>
                        ))}
                      </select>

                      <select
                        value={interviewSchedule.minute}
                        className={fieldErrors.minute ? "application-input-error" : ""}
                        onChange={(event) =>
                          handleInterviewFieldChange("minute", event.target.value)
                        }
                      >
                        <option value="">Minute</option>
                        {MINUTE_OPTIONS.map((minute) => (
                          <option key={minute} value={minute}>
                            {minute}
                          </option>
                        ))}
                      </select>

                      <select
                        value={interviewSchedule.meridiem}
                        className={fieldErrors.meridiem ? "application-input-error" : ""}
                        onChange={(event) =>
                          handleInterviewFieldChange("meridiem", event.target.value)
                        }
                      >
                        <option value="">AM/PM</option>
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                    {(fieldErrors.hour || fieldErrors.minute || fieldErrors.meridiem) && (
                      <p className="application-field-error">
                        {fieldErrors.hour ||
                          fieldErrors.minute ||
                          fieldErrors.meridiem}
                      </p>
                    )}
                  </div>

                  <div className="application-field-group">
                    <label
                      className="application-field-label"
                      htmlFor="interviewType"
                    >
                      Interview Mode
                    </label>
                    <select
                      id="interviewType"
                      value={interviewSchedule.interviewType}
                      onChange={(event) => {
                        const nextType = event.target.value;
                        setStatusError("");
                        setFieldErrors((prev) => ({
                          ...prev,
                          meetingLink: "",
                          location: "",
                        }));
                        setInterviewSchedule((prev) => ({
                          ...prev,
                          interviewType: nextType,
                        }));
                      }}
                    >
                      <option value="VIRTUAL">Virtual</option>
                      <option value="OFFLINE">Offline</option>
                    </select>
                  </div>

                  {interviewSchedule.interviewType === "VIRTUAL" ? (
                    <div className="application-field-group">
                      <label
                        className="application-field-label"
                        htmlFor="meetingLink"
                      >
                        Meeting Link
                      </label>
                      <input
                        id="meetingLink"
                        type="url"
                        value={interviewSchedule.meetingLink}
                        className={
                          fieldErrors.meetingLink ? "application-input-error" : ""
                        }
                        onChange={(event) =>
                          handleInterviewFieldChange(
                            "meetingLink",
                            event.target.value,
                          )
                        }
                        placeholder="https://meet.google.com/..."
                      />
                      {fieldErrors.meetingLink && (
                        <p className="application-field-error">
                          {fieldErrors.meetingLink}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="application-field-group">
                      <label
                        className="application-field-label"
                        htmlFor="interviewLocation"
                      >
                        Interview Location
                      </label>
                      <input
                        id="interviewLocation"
                        type="text"
                        value={interviewSchedule.location}
                        className={
                          fieldErrors.location ? "application-input-error" : ""
                        }
                        onChange={(event) =>
                          handleInterviewFieldChange("location", event.target.value)
                        }
                        placeholder="Enter office address"
                      />
                      {fieldErrors.location && (
                        <p className="application-field-error">
                          {fieldErrors.location}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {statusError && <p className="application-field-error">{statusError}</p>}

            <div className="application-detail-actions">
              <button
                type="button"
                className="application-primary-btn"
                disabled={!canSaveStatus || isSavingStatus}
                onClick={handleSaveStatus}
              >
                {isSavingStatus ? "Saving..." : "Save Status"}
              </button>

              <button
                type="button"
                className="application-secondary-btn"
                onClick={() => openResume(activeCandidate.resumeUrl)}
              >
                View Resume
              </button>

            </div>
          </aside>
        </section>
      </div>
    );
  }

  return (
    <div className="application-admin-page">
      <header className="application-admin-header">
        <div>
          <h2 className="application-admin-title">Application Management</h2>
          <p className="application-admin-subtitle">
            Review candidates, open details, and manage application progress.
          </p>
        </div>
      </header>

      <section className="application-summary-grid">
        <article className="application-summary-card">
          <span>Total Applications</span>
          <strong>{summary.total}</strong>
        </article>

        <article className="application-summary-card">
          <span>Shortlisted</span>
          <strong>{summary.shortlisted}</strong>
        </article>

        <article className="application-summary-card">
          <span>Selected</span>
          <strong>{summary.selected}</strong>
        </article>

        <article className="application-summary-card">
          <span>Rejected</span>
          <strong>{summary.rejected}</strong>
        </article>
      </section>

      <section className="application-filter-card">
        <input
          type="text"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search candidate name, email, role, or status"
        />

        <select
          value={jobFilter}
          onChange={(event) => setJobFilter(event.target.value)}
        >
          {jobOptions.map((job) => (
            <option key={job} value={job}>
              {job === "All" ? "All Jobs" : job}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
        >
          <option value="All">All Status</option>

          {STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {formatStatusLabel(status)}
            </option>
          ))}
        </select>
      </section>

      <section className="application-table-wrap">
        <table className="application-table">
          <thead>
            <tr>
              <th>Candidate</th>
              <th>Applied Role</th>
              <th>Status</th>
              <th>Applied On</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {paginatedApplications.map((application) => (
              <tr key={application.applicationId}>
                <td>
                  <button
                    type="button"
                    className="application-candidate-button"
                    onClick={() => setActiveCandidateId(application.applicationId)}
                  >
                    <span className="application-candidate-name">
                      {application.fullName}
                    </span>
                    <span className="application-candidate-meta">
                      {application.email}
                    </span>
                    <span className="application-candidate-meta">
                      {application.phone}
                    </span>
                  </button>
                </td>

                <td>
                  <div className="application-role-cell">
                    <strong>{application.jobTitle}</strong>
                    <span>{application.jobLocation}</span>
                  </div>
                </td>

                <td>
                  <span
                    className={`application-status-badge ${getStatusBadgeClassName(
                      application.status,
                    )}`}
                  >
                    {formatStatusLabel(application.status)}
                  </span>
                </td>

                <td>{formatDateLabel(application.createdAt)}</td>

                <td className="application-action-cell">
                  <button
                    type="button"
                    className="application-open-btn"
                    onClick={() => setActiveCandidateId(application.applicationId)}
                  >
                    Open
                  </button>
                </td>
              </tr>
            ))}

            {filteredApplications.length === 0 && (
              <tr>
                <td colSpan="5">
                  <div className="application-empty-state">
                    No applications found for the selected criteria.
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {filteredApplications.length > 0 && (
        <div className="applicationmanagement-pagination">
          <button
            type="button"
            className="application-secondary-btn"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
          >
            Previous
          </button>

          <span className="application-pagination-status">
            Page {currentPage} of {totalPages}
          </span>

          <button
            type="button"
            className="application-secondary-btn"
            disabled={currentPage === totalPages}
            onClick={() =>
              setCurrentPage((page) => Math.min(totalPages, page + 1))
            }
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ApplicationManagement;
